<template>
    <div class="main">

        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item"><a :href="'/gmView/' + geneLocus">{{geneLocus}}</a></li>
                <li class="breadcrumb-item active" aria-current="page">{{rnaName}}</li>
            </ol>
        </nav>

        <h2 class="round">Germline Morphology Result</h2>

        <div class="mainContent">
            <fetch-sterility :rna-id="rnaId">
                <template slot-scope="{response, loading}">
                    <div v-if="loading">Loading...</div>
                    <div v-else-if="Object.keys(response).length > 0">
                        <h4 class="round">Sterility Test</h4>
                        <div class="panel-body">
                            <div class="container-fluid">
                                <div class="row">
                                    <div class="list-group-item-heading col-sm-2">Sterility Test:<br/>
                                        (<a href="/explainGonadSterility">explain</a>)
                                    </div>
                                    <div class="col-sm-10">{{response.name}}</div>
                                </div>
                                <div class="row">
                                    <div class="list-group-item-heading col-sm-2">Comment:</div>
                                    <div class="col-sm-10">{{response.comment}}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </fetch-sterility>

            <fetch-movie :rna-id="rnaId">
                <template slot-scope="{response, loading}">
                    <div v-if="loading">Loading...</div>
                    <div v-else-if="Object.keys(response).length > 0">
                        <div class="row">
                            <div class="col-sm-6">
                                <h4 class="round">Recordings (Movies)</h4>
                                <div class="panel panel-default table-responsive">
                                    <table class="table table-striped table-hover">
                                        <thead>
                                        <tr>
                                            <th>Movies</th>
                                            <th></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="(row, index) in response" v-bind:key="index">
                                            <th scope="row" v-bind:class="{ 'playing': currentIndex === index }">
                                                {{index + 1}}
                                                <template v-if="checkMovie(index, row.movieFile)">
                                                <button v-on:click="playMovie(index, row.movieFile)">
                                                    <i class="glyphicon glyphicon-play" aria-hidden="true"></i>
                                                </button>
                                                </template>
                                            </th>
                                            <td v-bind:class="{ 'playing': currentIndex === index }">
                                                <img :src="getContent(row.stillFile)"/>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                    <a href="/controlMovies"><div class="blue">View control movies</div></a>
                                </div>
                            </div>

                            <div class="col-sm-6">
                                <h4 class="round">Recording {{currentIndex + 1}}</h4>
                                <div class="panel panel-default">

                                    <video ref="video" controls loop autoplay>
                                        <source ref="source" :src="current.src" type="video/mp4">
                                        Sorry, your browser doesn't support embedded videos.
                                    </video>
                                    <a :href="current.src">
                                        <div class="blue">Link to Movie</div>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </fetch-movie>

            <fetch-pheno-info :rna-id="rnaId">
                <template slot-scope="{response, loading}">
                    <div v-if="loading">Loading...</div>
                    <div v-else-if="Object.keys(response).length > 0">

                        <h4 class="round">Morphology</h4>
                        <div class="panel panel-default">
                            <div class="panel-body">
                                <div class="container-fluid col-md-7">
                                    <div class="row">
                                        <div class="text col-sm-2">Broad Phenotypic Category:</div>
                                        <div class="col-sm-10">
                                            <a :href="'/broad_category/' + response.phenoCategoryCode + '.xls'">{{response.phenoCategoryCode}} - {{response.phenoCategoryName}}</a>
                                            <br/>
                                            <fetch-broad-pheno-genes :class-id="response.classId" :pheno-id="response.phenoId">
                                                <template slot-scope="{response, loading, classId, phenoId}">
                                                    <div v-if="loading">Loading...</div>
                                                    <div v-else-if="response.length > 0" class="d-flex justify-content-between align-items-center">
                                                        <a :href="'/gmPhenoGenesView/' + classId + '/' + phenoId">
                                                        <span class="label label-primary">{{response.length}} Genes</span>
                                                        </a>
                                                        (click to view genes in broad phenotypic category)
                                                    </div>
                                                </template>
                                            </fetch-broad-pheno-genes>
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="text col-sm-2">Phenotypic Class:</div>
                                        <div class="col-sm-10">
                                            {{response.phenoClassCode}} - {{response.phenoClassDescription}}
                                            <br/>
                                            <fetch-pheno-genes :class-id="response.classId" :pheno-id="response.phenoId">
                                                <template slot-scope="{response, loading, classId, phenoId}">
                                                    <div v-if="loading">Loading...</div>
                                                    <div v-else-if="response.length > 0" class="d-flex justify-content-between align-items-center">
                                                        <a :href="'/gmPhenoGenesView/' + classId + '/' + phenoId">
                                                        <span class="label label-primary">{{response.length}} Genes</span>
                                                        </a>
                                                        (click to view genes in phenotypic class)
                                                    </div>
                                                </template>
                                            </fetch-pheno-genes>
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="text col-sm-2">Description:</div>
                                        <div class="col-sm-10">{{response.phenoDescription}}</div>
                                    </div>
                                    <div class="row">
                                        <div class="text col-sm-2">Comments:</div>
                                        <div class="col-sm-10">{{response.comment}}</div>
                                    </div>
                                    <div class="row">
                                        <div class="text col-sm-2">Defects:<br/>
                                            (<a href="/explainGonadDefects">explain</a>)
                                        </div>
                                        <div class="col-sm-10 ">
                                            <div class="list-group">
                                                <div v-for="(item, index) in response.defects" v-bind:key="index" class="list-group-item list-group-item-action flex-column align-items-start">
                                                    <div class="d-flex w-100 justify-content-between">
                                                    <h5 class="mb-1">
                                                        {{key = Object.keys(item)[0]}}
                                                    </h5>
                                                    </div>
                                                    <ul>
                                                        <li v-for="(subItem, subIdx) in item[key]" v-bind:key="index + '-' + subIdx" class="small">
                                                            {{subItem}}
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="container-fluid col-md-5">
                                    <img :src="getGermlineSvg(rnaName)"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </fetch-pheno-info>
        </div>
    </div>
</template>

<script>
  import FetchSterility from './FetchSterility.vue'
  import FetchPhenoInfo from './FetchPhenoInfo.vue'
  import FetchMovie from './FetchMovie.vue'
  import FetchBroadPhenoGenes from './FetchBroadPhenoGenes'
  import FetchPhenoGenes from './FetchPhenoGenes'

  const _ = require('lodash');

  export default {
    name: "ResultView",
    components: {
      FetchSterility, FetchPhenoInfo, FetchMovie, FetchBroadPhenoGenes, FetchPhenoGenes
    },
    data() {
      return {
        currentIndex: 0,
        current: {},
        currentType: '',
        geneLocus: this.$route.params.geneLocus,
        rnaName: this.$route.params.rnaName,
        rnaId: this.$route.params.rnaId,
        publicPath: process.env.BASE_URL
      }
    },
    methods: {
      playMovie(idx, name) {
        const vm = this
        vm.currentIndex = idx
        vm.current.name = name

        vm.$refs.video.firstChild.src = vm.getContent(name.replace('.mov', '') + '.mp4')
        vm.$refs.video.load()
        vm.current.src = vm.getContent(name.replace('.mov', '') + '.mp4')
      },
      checkMovie(idx, name) {
        const vm = this
        if(name && !_.isEmpty(name)) {
          vm.current['name'] = name
          if(vm.current['src'] === undefined) {
              vm.current['src'] = vm.getContent(name.replace('.mov', '') + '.mp4')
          }
          return true;
        }
        else return false;
      },
      getContent(url) {
        return `/gonad_movies/${url}`
      },
      getGermlineSvg(rnaName) {
        return `/Germline/${rnaName}/${rnaName}_manual.svg`
      }
    },
    mounted() {
      // const vm = this
      // vm.images = []
      // /* eslint-disable no-console */
      // console.log(vm.dsRnaName);
    },
  }
</script>

<style>
    @import url('~@/assets/bootstrap.css');
    @import url('~@/assets/datatable.css');

    .important {
        border: 1px solid #aaaaaa;
        padding: 0.3em;
        margin-bottom: 1em;
        background-color: #EEB8B4;
    }

    li {
        padding-left: 10px;
    }

    .main {
        margin: 1.5rem;
    }

    .clear {
        background-color: #c7eedc;
    }

    .ambiguous {
        background-color: #b1aeb4;
    }

    .severe {
        background-color: #ee9c97;
    }

    .playing {
        background-color: lightgreen;
    }

    .container {
        position: relative;
        text-align: center;
        color: white;
    }

    .body {
        font-weight: bold;
        font-size: 12px;
    }

    .breadcrumb {
        font-size: 1.3rem;
    }

    .breadcrumb a {
        display: inline;
        padding: 0 0.5em;
    }

    .row {
        font-size: 1.2rem;
        padding-bottom: 8px;
    }

    .centered {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-weight: bold;
        font-size: 20px;
        text-decoration: underline;
    }

    .blue {
        color: #2834ff;
    }

    .white {
        color: white;
    }

    .round {
        font-family: Calibri, Candara, Segoe, Segoe UI, Optima, Arial;
        border-radius: 7px;
        border: 1px solid #d9d9d9;
        background: #dae3f3;
        padding: 3px 7px 3px 7px;
        font-style: oblique;
        color: #203864;
        font-weight: bold;
    }

    .text {
        font-weight: bold;
        color: #203864;
        padding-bottom: 10px;
    }

    p.strong {
        text-decoration: underline;
        font-style: oblique;
    }

    h2.card-title {
        margin-top: 0;
        color: #2f5597;
        font-style: oblique;
    }

    h5.card-title {
        color: #2f5597;
        font-style: oblique;
        font-weight: bold;
    }

    .mainContent {
        border-radius: 25px;
        border: 2px solid #dae3f3;
        padding: 20px;
    }
</style>